import { useState, useEffect, useCallback, useRef } from 'react';
import Video from 'twilio-video';

export const usePreviewTracks = (selectedVideoDeviceId, selectedAudioDeviceId, permissionsGranted = false) => {
  const [previewTracks, setPreviewTracks] = useState({ video: null, audio: null });
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const previewVideoRef = useRef(null);

  const initializePreview = useCallback(async () => {
    if (!selectedVideoDeviceId || !selectedAudioDeviceId) return;

    setIsPreviewLoading(true);
    setPreviewError(null);
    
    try {
      console.log('Re-initializing preview with devices:', { selectedVideoDeviceId, selectedAudioDeviceId });
      
      const tracks = await Video.createLocalTracks({
        audio: { deviceId: { exact: selectedAudioDeviceId } },
        video: { 
          deviceId: { exact: selectedVideoDeviceId },
          width: { min: 640, ideal: 1280, max: 1920 }, 
          height: { min: 480, ideal: 720, max: 1080 }, 
          frameRate: { min: 15, ideal: 30, max: 60 }
        }
      });
      
      const videoTrack = tracks.find(t => t.kind === 'video');
      const audioTrack = tracks.find(t => t.kind === 'audio');
      
      console.log('Re-created tracks:', { video: !!videoTrack, audio: !!audioTrack });
      
      setPreviewTracks({ video: videoTrack, audio: audioTrack });
      
      // Set initial states based on tracks
      setIsVideoEnabled(!!videoTrack);
      setIsAudioEnabled(!!audioTrack);
      
    } catch (err) {
      console.error('Error re-initializing preview:', err);
      setPreviewError('Unable to access camera or microphone. Please check your permissions.');
    } finally {
      setIsPreviewLoading(false);
    }
  }, [selectedVideoDeviceId, selectedAudioDeviceId]);

  const cleanupPreviewTracks = useCallback((stopTracks = true) => {
    console.log('Cleaning up preview tracks, stopTracks:', stopTracks);
    setPreviewTracks(currentTracks => {
      if (stopTracks) {
        if (currentTracks.video) {
          currentTracks.video.stop();
        }
        if (currentTracks.audio) {
          currentTracks.audio.stop();
        }
      }
      return { video: null, audio: null };
    });
  }, []);

  const refreshPreview = useCallback(() => {
    cleanupPreviewTracks();
    initializePreview();
  }, [cleanupPreviewTracks, initializePreview]);

  const togglePreviewVideo = useCallback(() => {
    if (previewTracks.video) {
      if (isVideoEnabled) {
        previewTracks.video.disable();
      } else {
        previewTracks.video.enable();
      }
      setIsVideoEnabled(!isVideoEnabled);
    }
  }, [previewTracks.video, isVideoEnabled]);

  const togglePreviewAudio = useCallback(() => {
    if (previewTracks.audio) {
      if (isAudioEnabled) {
        previewTracks.audio.disable();
      } else {
        previewTracks.audio.enable();
      }
      setIsAudioEnabled(!isAudioEnabled);
    }
  }, [previewTracks.audio, isAudioEnabled]);



  // Initialize preview tracks when devices change
  useEffect(() => {
    let isActive = true; // Flag to prevent state updates after cleanup
    
    const setupPreview = async () => {
      if (!permissionsGranted || !selectedVideoDeviceId || !selectedAudioDeviceId) {
        console.log('Skipping preview setup:', { permissionsGranted, selectedVideoDeviceId: !!selectedVideoDeviceId, selectedAudioDeviceId: !!selectedAudioDeviceId });
        return;
      }

      setIsPreviewLoading(true);
      setPreviewError(null);
      
      try {
        console.log('Initializing preview with devices:', { selectedVideoDeviceId, selectedAudioDeviceId });
        
        const tracks = await Video.createLocalTracks({
          audio: { deviceId: { exact: selectedAudioDeviceId } },
          video: { 
            deviceId: { exact: selectedVideoDeviceId },
            width: { min: 640, ideal: 1280, max: 1920 }, 
            height: { min: 480, ideal: 720, max: 1080 }, 
            frameRate: { min: 15, ideal: 30, max: 60 }
          }
        });
        
        if (!isActive) {
          // Component unmounted, cleanup tracks
          tracks.forEach(track => track.stop());
          return;
        }
        
        const videoTrack = tracks.find(t => t.kind === 'video');
        const audioTrack = tracks.find(t => t.kind === 'audio');
        
        console.log('Created tracks:', { video: !!videoTrack, audio: !!audioTrack });
        
        setPreviewTracks({ video: videoTrack, audio: audioTrack });
        
        // Set initial states based on tracks
        setIsVideoEnabled(!!videoTrack);
        setIsAudioEnabled(!!audioTrack);
        
      } catch (err) {
        if (isActive) {
          console.error('Error initializing preview:', err);
          setPreviewError('Unable to access camera or microphone. Please check your permissions.');
        }
      } finally {
        if (isActive) {
          setIsPreviewLoading(false);
        }
      }
    };

    if (permissionsGranted && selectedVideoDeviceId && selectedAudioDeviceId) {
      setupPreview();
    }
    
    // Cleanup on unmount or device change
    return () => {
      isActive = false;
      setPreviewTracks(currentTracks => {
        if (currentTracks.video) {
          currentTracks.video.stop();
        }
        if (currentTracks.audio) {
          currentTracks.audio.stop();
        }
        return { video: null, audio: null };
      });
    };
  }, [permissionsGranted, selectedVideoDeviceId, selectedAudioDeviceId]);

  return {
    previewTracks,
    isPreviewLoading,
    previewError,
    isVideoEnabled,
    isAudioEnabled,
    previewVideoRef,
    togglePreviewVideo,
    togglePreviewAudio,
    refreshPreview,
    cleanupPreviewTracks
  };
}; 